import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EDGE, EDGE_LABEL, ENERGY, ENERGY_LABEL, gridDefaults, gridReport, zoneFor, type GridInputs, type GridRow } from "@/lib/tools/calc";
import { ToolPageShell, toolHead } from "@/components/site/tools/ToolPageShell";
import { GridChart } from "@/components/site/tools/ToolCharts";

export const Route = createFileRoute("/tools/time-and-accountability-grid")({
  head: () => toolHead("time-and-accountability-grid"),
  component: TimeGridPage,
});

const select = "h-11 w-full rounded-md border border-field-border bg-card px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function TimeGridPage() {
  const [i, setI] = useState<GridInputs>(gridDefaults);
  const [next, setNext] = useState(gridDefaults.rows.length + 1);
  const update = (id: string, patch: Partial<GridRow>) =>
    setI((p) => ({ ...p, rows: p.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
  const remove = (id: string) => setI((p) => ({ rows: p.rows.filter((r) => r.id !== id), ticked: p.ticked.filter((t) => t !== id) }));
  const add = () => {
    setI((p) => ({ ...p, rows: [...p.rows, { id: `r${next}`, name: "", energy: "neutral", edge: "competent", hours: 1 }] }));
    setNext((n) => n + 1);
  };
  const toggle = (id: string) =>
    setI((p) => ({ ...p, ticked: p.ticked.includes(id) ? p.ticked.filter((t) => t !== id) : p.ticked.length >= 3 ? p.ticked : [...p.ticked, id] }));

  const handOff = i.rows.filter((r) => r.name.trim() && ["Hand Over", "Let Go"].includes(zoneFor(r.energy, r.edge)));

  return (
    <ToolPageShell
      slug="time-and-accountability-grid"
      report={gridReport(i)}
      inputs={i}
      chart={<GridChart inputs={i} />}
      lead="List what you spent time on in the last fortnight. Rate each one on Energy and Edge. The grid shows what to keep and what to hand over, in hours."
      form={
        <div>
          <ul className="space-y-4">
            {i.rows.map((r, k) => (
              <li key={r.id} className="rounded-xl border border-border bg-surface p-3">
                <fieldset>
                  <legend className="sr-only">Activity {k + 1}</legend>
                  <div className="flex gap-2">
                    <div className="min-w-0 flex-1">
                      <label htmlFor={`${r.id}-n`} className="text-xs font-medium text-charcoal">Activity</label>
                      <Input id={`${r.id}-n`} value={r.name} maxLength={120} onChange={(e) => update(r.id, { name: e.target.value })} className="mt-1 h-11 border-field-border bg-card" />
                    </div>
                    <button type="button" onClick={() => remove(r.id)} aria-label={`Remove ${r.name || `activity ${k + 1}`}`} className="mt-5 grid h-11 w-11 shrink-0 place-items-center rounded-md border border-border bg-card text-charcoal hover:text-destructive focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div>
                      <label htmlFor={`${r.id}-e`} className="text-xs font-medium text-charcoal">Energy</label>
                      <select id={`${r.id}-e`} value={r.energy} onChange={(e) => update(r.id, { energy: e.target.value as GridRow["energy"] })} className={`mt-1 ${select}`}>
                        {ENERGY.map((x) => <option key={x} value={x}>{ENERGY_LABEL[x]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`${r.id}-g`} className="text-xs font-medium text-charcoal">Edge</label>
                      <select id={`${r.id}-g`} value={r.edge} onChange={(e) => update(r.id, { edge: e.target.value as GridRow["edge"] })} className={`mt-1 ${select}`}>
                        {EDGE.map((x) => <option key={x} value={x}>{EDGE_LABEL[x]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`${r.id}-h`} className="text-xs font-medium text-charcoal">Hours a week</label>
                      <Input id={`${r.id}-h`} type="number" inputMode="decimal" min={0} max={100} step="0.5" value={r.hours} onChange={(e) => update(r.id, { hours: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} className="mt-1 h-11 border-field-border bg-card" />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-charcoal">Zone: <span className="font-semibold text-midnight">{zoneFor(r.energy, r.edge)}</span></p>
                </fieldset>
              </li>
            ))}
          </ul>
          <button type="button" onClick={add} disabled={i.rows.length >= 40} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-full border border-midnight px-5 text-sm font-semibold text-midnight hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
            <Plus className="h-4 w-4" aria-hidden="true" /> Add an activity
          </button>

          {handOff.length ? (
            <fieldset className="mt-6 border-t border-border pt-5">
              <legend className="font-semibold text-midnight">Pick up to three to hand over this quarter</legend>
              <p className="mt-1 text-sm text-charcoal">{i.ticked.length} of 3 ticked. These become your three actions.</p>
              <ul className="mt-3 space-y-2">
                {handOff.map((r) => {
                  const checked = i.ticked.includes(r.id);
                  return (
                    <li key={r.id}>
                      <label className="flex items-center gap-3 text-sm">
                        <input type="checkbox" checked={checked} disabled={!checked && i.ticked.length >= 3} onChange={() => toggle(r.id)} className="h-4 w-4 accent-midnight" />
                        <span>{r.name} <span className="text-charcoal">({zoneFor(r.energy, r.edge)}, {r.hours} hrs)</span></span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          ) : null}
        </div>
      }
    />
  );
}
